import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/
import { useState, useContext } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField, Autocomplete, Box, Avatar } from '@mui/material';
import { MainContext } from '../context';
import { useDebouncedCallback } from '../hooks/use-debounced-callback';
import { useUsersByName } from './use-users-by-name';
export const AddUserDialog = ({ open, title, description, existingUsers, onClose, onAddUser, filterUsers: externalFilter }) => {
    const { handleError } = useContext(MainContext);
    const [inputValue, setInputValue] = useState('');
    const [searchName, setSearchName] = useState('');
    const debouncedSetSearchName = useDebouncedCallback(setSearchName);
    const { data: users, isFetching } = useUsersByName(searchName, {
        enabled: !!searchName,
        placeholderData: keepPreviousData
    });
    const options = inputValue && users ? users : [];
    // Loading spans the debounce too, so the list doesn't read "No options" while the user types.
    const loading = !!inputValue && (inputValue !== searchName || isFetching);
    const isUserExcluded = (user) => existingUsers.some(u => u.loginName === user.loginName && u.provider === user.provider) ||
        (externalFilter && !externalFilter(user));
    const handleInputChange = (_, value) => {
        setInputValue(value);
        if (value) {
            debouncedSetSearchName(value);
        }
        else {
            debouncedSetSearchName.cancel();
            setSearchName('');
        }
    };
    const resetSearch = () => {
        debouncedSetSearchName.cancel();
        setInputValue('');
        setSearchName('');
    };
    const handleSelect = (_, user) => {
        if (!user)
            return;
        if (isUserExcluded(user)) {
            handleError({ message: `User ${user.loginName} is already added.` }, { title: 'Couldn’t add the user' });
            return;
        }
        onAddUser(user);
        resetSearch();
        onClose();
    };
    const handleClose = () => {
        resetSearch();
        onClose();
    };
    return (_jsxs(Dialog, { onClose: handleClose, open: open, maxWidth: 'sm', fullWidth: true, children: [_jsx(DialogTitle, { children: title }), _jsxs(DialogContent, { children: [_jsx(DialogContentText, { children: description }), _jsx(Autocomplete, { options: options, loading: loading, filterOptions: opts => opts.filter(u => !isUserExcluded(u)), getOptionLabel: option => option.loginName, isOptionEqualToValue: (option, value) => option.loginName === value.loginName && option.provider === value.provider, onInputChange: handleInputChange, onChange: handleSelect, renderOption: (props, user) => (_createElement(Box, { component: 'li', ...props, key: user.loginName + user.provider, sx: { display: 'flex', alignItems: 'center', gap: 1.5 } },
                            _jsx(Avatar, { variant: 'rounded', src: user.avatarUrl, sx: { width: 36, height: 36 } }),
                            _jsxs(Box, { children: [_jsx(Box, { fontWeight: 'bold', children: user.loginName }), _jsx(Box, { fontSize: '0.75rem', color: 'text.secondary', children: user.fullName })] }))), renderInput: params => (_jsx(TextField, { ...params, autoFocus: true, margin: 'dense', label: 'Search User', fullWidth: true })) })] }), _jsx(DialogActions, { children: _jsx(Button, { onClick: handleClose, children: "Cancel" }) })] }));
};
//# sourceMappingURL=add-user-dialog.js.map